import { type ReactNode, useEffect, useMemo } from "react";
import { useDroppable } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { cn } from "@/lib/utils";
import { trpc } from "@/trpc";
import type { PipelinePosition } from "@/contexts/PipelineContext";
import ProspectoCard from "./ProspectoCard";
import {
  buildColumnQueryInput,
  headerColorForStage,
  type TableroLead,
  type TableroQueryFilters,
} from "./types";

type ColumnaProps = {
  title: string;
  accent: string;
  countLabel: string;
  droppableId: string;
  droppableDisabled: boolean;
  isLoading: boolean;
  // Optional: only the non-realtime board threads its query error this far. A
  // column that fails must not be pixel-identical to a genuinely empty one.
  isError?: boolean;
  // Feeds SortableContext — leads on the lead board, solicitudes on the
  // ticket board. The column itself is domain-agnostic.
  itemIds: string[];
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
  emptyText?: string;
  children: ReactNode;
};

// Presentational column, shared by the per-position infinite queries and the
// phone-search grouped mode.
export const Columna = ({
  title,
  accent,
  countLabel,
  droppableId,
  droppableDisabled,
  isLoading,
  isError,
  itemIds,
  hasMore,
  loadingMore,
  onLoadMore,
  emptyText = "Sin leads",
  children,
}: ColumnaProps) => {
  const { setNodeRef, isOver } = useDroppable({ id: droppableId, disabled: droppableDisabled });

  return (
    <div className="flex w-[280px] shrink-0 flex-col rounded-md bg-neutral-100/50">
      <div className={cn("flex h-10 items-center justify-between rounded-t-md px-3", accent)}>
        <span className="truncate text-sm font-semibold text-neutral-900">{title}</span>
        <span className="rounded-pill bg-white/60 px-2 py-0.5 text-xs font-medium text-neutral-900">
          {countLabel}
        </span>
      </div>
      <div
        ref={setNodeRef}
        className={cn(
          "min-h-[200px] flex-1 space-y-2 overflow-y-auto p-3 transition-colors",
          isOver && !droppableDisabled && "bg-palomma-primary/10"
        )}
      >
        <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
          {children}
        </SortableContext>
        {isLoading ? (
          <div className="py-8 text-center text-xs text-neutral-600">Cargando…</div>
        ) : isError ? (
          <div className="py-8 text-center text-xs text-red-600">
            No pudimos cargar esta columna.
          </div>
        ) : itemIds.length === 0 ? (
          <div className="py-8 text-center text-xs text-neutral-600">{emptyText}</div>
        ) : null}
        {hasMore ? (
          <button
            onClick={onLoadMore}
            disabled={loadingMore}
            className="min-h-11 w-full rounded-md border border-neutral-300 bg-surface text-xs font-medium text-neutral-600 transition-colors hover:bg-neutral-100 disabled:opacity-50"
          >
            {loadingMore ? "Cargando…" : "Cargar más"}
          </button>
        ) : null}
      </div>
    </div>
  );
};

type Props = {
  merchantId: string;
  pipelineId: string;
  position: PipelinePosition;
  filters: TableroQueryFilters;
  // "hidden" keeps the query mounted without rendering (Lista view collects
  // the same per-column pages through onLeadsChange).
  mode: "kanban" | "hidden";
  // Column-level: is this a human-managed stage. A card in a WhatsApp column is
  // still draggable when no AI agent owns its lead — see the KanbanBoard guard.
  draggable: boolean;
  droppable: boolean;
  onSelectLead: (leadId: string) => void;
  selectedLeadId?: string;
  employeeNameById: Map<string, string>;
  onLeadsChange?: (positionId: string, leads: TableroLead[], isLoading: boolean) => void;
};

const PositionColumn = ({
  merchantId,
  pipelineId,
  position,
  filters,
  mode,
  draggable,
  droppable,
  onSelectLead,
  selectedLeadId,
  employeeNameById,
  onLeadsChange,
}: Props) => {
  const queryInput = buildColumnQueryInput(merchantId, pipelineId, position.id, filters);
  const query = trpc.leads.listActiveByPosition.useInfiniteQuery(queryInput, {
    getNextPageParam: (lastPage) => lastPage.cursor ?? undefined,
  });

  const leads = useMemo(() => query.data?.pages.flatMap((p) => p.data) ?? [], [query.data]);

  useEffect(() => {
    onLeadsChange?.(position.id, leads, query.isLoading);
  }, [position.id, leads, query.isLoading, onLeadsChange]);

  if (mode === "hidden") return null;

  return (
    <Columna
      title={position.displayName}
      accent={headerColorForStage(position.stage)}
      countLabel={`${leads.length}${query.hasNextPage ? "+" : ""}`}
      droppableId={`position:${position.id}`}
      droppableDisabled={!droppable}
      isLoading={query.isLoading}
      itemIds={leads.map((l) => l.id)}
      hasMore={!!query.hasNextPage}
      loadingMore={query.isFetchingNextPage}
      onLoadMore={() => query.fetchNextPage()}
    >
      {leads.map((lead) => (
        <ProspectoCard
          key={lead.id}
          lead={lead}
          selected={selectedLeadId === lead.id}
          draggable={draggable || !lead.assignedAgentId}
          onClick={() => onSelectLead(lead.id)}
          employeeNameById={employeeNameById}
        />
      ))}
    </Columna>
  );
};

export default PositionColumn;
